import type { Config } from '@backstage/config';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { GithubTeam } from '../types';

/**
 * A helper that converts the result of a render('name', opts) to make it compatible with material-ui.
 *
 * See also https://github.com/react-hook-form/react-hook-form/issues/4629#issuecomment-815840872
 * TODO: remove when updating to material-ui v5 (https://github.com/mui-org/material-ui/pull/23174)
 *
 * @param renderResult - the result of a render('name', opts)
 */
export function asInputRef(renderResult: UseFormRegisterReturn) {
  const { ref, ...rest } = renderResult;
  return {
    inputRef: ref,
    ...rest,
  };
}

export function getCatalogFilename(config: Config): string {
  return (
    config.getOptionalString('catalog.import.entityFilename') ??
    'catalog-info.yaml'
  );
}

export function getBranchName(config: Config): string {
  return (
    config.getOptionalString('catalog.import.pullRequestBranchName') ??
    'backstage-integration'
  );
}

type CatalogClientError = {
  message?: string;
  response?: { status?: number };
  body?: { error?: { message?: string } };
};

/** Extract a message string from a catalog API / fetch error. */
export function getCatalogClientErrorMessage(error: unknown): string {
  const err = error as CatalogClientError;
  return (
    err?.body?.error?.message ??
    err?.message ??
    'An unexpected error occurred.'
  );
}

/** True when the catalog API denied a refresh (403). */
export function isCatalogRefreshForbiddenError(error: unknown): boolean {
  const err = error as CatalogClientError;
  if (err?.response?.status === 403) {
    return true;
  }
  const message = getCatalogClientErrorMessage(error).toLowerCase();
  return message.includes('403') || message.includes('forbidden');
}

export async function fetchGithubTeams(
  repoUrl: string,
  githubToken: string,
): Promise<{ teamSlugs: string[]; error?: string }> {
  const match = repoUrl.match(/github\.com[:/](.+?)\/(.+?)(\.git)?$/);
  if (!match) return { teamSlugs: [], error: 'Invalid GitHub repository URL.' };
  const [, org, repo] = match;
  const response = await fetch(
    `https://api.github.com/repos/${org}/${repo}/teams`,
    {
      headers: {
        Authorization: `token ${githubToken}`,
        Accept: 'application/vnd.github.v3+json',
      },
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    return {
      teamSlugs: [],
      error: `GitHub API error: ${response.status} ${response.statusText} - ${errorText}`,
    };
  }
  const teams: GithubTeam[] = await response.json();

  const permissionPriority = ['admin', 'maintain', 'push'];

  for (const permission of permissionPriority) {
    const team = teams.find(t => t.permission === permission);
    if (team) {
      return { teamSlugs: [team.slug] };
    }
  }
  return { teamSlugs: [] };
}
